"use client"

import { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"

export default function Services() {
  const [activeSection, setActiveSection] = useState(0)
  const sectionRefs = useRef([])

  const sections = [
    {
      title: "Built for Small Bay & Flex Industrial",
      desc: "Automate rent collection, late fees, and payment reminders across your entire multi-tenant portfolio. Accept ACH, credit card, and check — and get paid on time without chasing tenants.",
      color: "bg-[#0381CA0F]",
      bgColor: "#0381CA0F",
      borderColor: "border-blue-400",
      textColor: "text-blue-600",
      image: "/assets/home/section1.png",
      points: [
        "Accept ACH, credit card, debit, and check payments",
        "Automated late-fee enforcement and payment reminders",
        "Real-time occupancy tracking across all unit types",
      ],
    },
    {
      title: "Accounting Built for NNN Leases",
      desc: "Run a full accounting suite purpose-built for industrial property managers. Generate CAM reconciliations, track expense pass-throughs, and produce custom financial reports — without a separate bookkeeping tool.",
      color: "bg-orange-50",
      bgColor: "#fff7ed",
      borderColor: "border-orange-400",
      textColor: "text-btnColor",
      image: "/assets/home/section2.png",
      points: [
        "CAM reconciliation and expense pass-through tracking",
        "Customizable chart of accounts for industrial portfolios",
        "Bank sync and QuickBooks integration included",
      ],
    },
    {
      title: "Every Lease, Unit, and Tenant in One Place",
      desc: "Centralize lease agreements, maintenance logs, tenant communications, and renewal timelines. Set automated reminders so no critical date is ever missed across your flex space portfolio.",
      color: "bg-[#0381CA0F]",
      bgColor: "#0381CA0F",
      borderColor: "border-blue-400",
      textColor: "text-blue-600",
      image: "/assets/home/section3.png",
      points: [
        "Centralized lease storage with automated renewal alerts",
        "Maintenance request tracking from submission to close",
        "Direct tenant communication with a full audit trail",
      ],
    },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const windowHeight = window.innerHeight

      let currentSection = 0

      sectionRefs.current.forEach((section, index) => {
        if (!section) return

        const sectionTop = section.offsetTop
        const sectionBottom = sectionTop + section.offsetHeight

        // Trigger only when full section is visible (or top is at/above viewport and bottom is at/below)
        if (
          scrollTop >= sectionTop &&
          scrollTop + windowHeight >= sectionBottom
        ) {
          currentSection = index
        }
      })

      setActiveSection(currentSection)
    }

    window.addEventListener("scroll", handleScroll)
    setTimeout(handleScroll, 100)

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        duration: 0.8,
      },
    },
  }

  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen px-4 lg:px-24 mt-14">
      {/* Left: Scrollable Sections */}
      <div className="w-full lg:w-1/2">
        {sections.map((section, index) => (
          <motion.div
            key={index}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={`min-h-screen flex flex-col justify-center p-8 lg:p-14 ${section.color} ${
              index !== sections.length - 1 ? "border-b-2 border-gray-100" : ""
            }`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={sectionVariants}
          >
            {/* Mobile image */}
            <motion.div className="lg:hidden mb-8" variants={imageVariants}>
              <motion.img
                src={section.image}
                alt={section.title}
                className="w-full rounded-2xl shadow-xl"
                variants={imageVariants}
              />
            </motion.div>

            {/* Section number badge */}
            <motion.span
              className={`font-popinsSemiBold text-xs uppercase tracking-widest mb-4 ${section.textColor} opacity-60`}
              variants={itemVariants}
            >
              0{index + 1} / 03
            </motion.span>

            <motion.h2
              className={`text-3xl md:text-4xl lg:text-5xl font-popinsBold mb-5 leading-tight ${section.textColor}`}
              variants={itemVariants}
            >
              {section.title}
            </motion.h2>

            <motion.p className="font-popinsRegular text-base md:text-lg text-paraColor leading-relaxed pt-2" variants={itemVariants}>
              {section.desc}
            </motion.p>

            <motion.ul className="pt-6 font-popinsRegular space-y-4" variants={itemVariants}>
              {section.points.map((point, i) => (
                <motion.li key={i} className="flex items-start gap-3" variants={listItemVariants} custom={i}>
                  <span className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold ${index === 1 ? "bg-btnColor" : "bg-themeColor"}`}>✓</span>
                  <span className="text-base text-darkColor">{point}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        ))}
      </div>

      {/* Right: Sticky Image */}
      <div className="hidden lg:block lg:w-1/2 sticky top-0 h-screen">
        <motion.div
          className="h-full flex items-center justify-center"
          animate={{
            backgroundColor: sections[activeSection].bgColor,
          }}
          transition={{ duration: 0.8 }}
        >
          {sections.map((section, index) => (
            <motion.img
              key={index}
              src={section.image}
              alt={section.title}
              className="size-full rounded-lg object-contain absolute mr-[-180px]"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: activeSection === index ? 1 : 0,
                scale: activeSection === index ? 1 : 0.8,
                zIndex: activeSection === index ? 10 : 0,
              }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
                duration: 0.5,
              }}
            />
          ))}
        </motion.div>
      </div>
    </div>
  )
}
